import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { ChecklistFillDialog } from "@/components/ChecklistFillDialog";
import { ManufacturerSelect } from "@/components/ManufacturerSelect";
import { CatalogSelect } from "@/components/CatalogSelect";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Barcode, CheckCircle, Plus, ArrowRight, ArrowLeft, Stack, PuzzlePiece, Camera, CloudArrowUp, WifiSlash, ListChecks, DownloadSimple } from "@phosphor-icons/react";
import { enqueue, dataUrlToBlob } from "@/lib/offlineQueue";
import { cachedGet, prepareSite, getPreparedStamp } from "@/lib/offlineCache";
import { useOfflineSync } from "@/context/OfflineSyncContext";
import { toast } from "sonner";

const STEPS = ["Anlage", "Art & Typ", "Typenschild", "Speichern"];

export default function Erfassung() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = localStorage.getItem("csp_token");
  const [step, setStep] = useState(0);
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [assetId, setAssetId] = useState(params.get("asset") || "");
  const [artKey, setArtKey] = useState("");
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("Hauptschild");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [sel, setSel] = useState({});
  const [fields, setFields] = useState({ bezeichnung: "", manufacturer: "", modell: "", serial_no: "", auftragsnummer: "", baujahr: "" });
  const [specs, setSpecs] = useState({});
  const [saved, setSaved] = useState(0);
  const [captured, setCaptured] = useState([]);
  const loadCaptured = (aid) => { if (!aid) { setCaptured([]); return; } cachedGet(`/v1/assets/${aid}/components`).then((d) => setCaptured(d || [])).catch(() => setCaptured([])); };
  const { pending, online, sync } = useOfflineSync();
  const [optionLists, setOptionLists] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [status, setStatus] = useState("");
  const [fillOpen, setFillOpen] = useState(false);
  const [fillComponent, setFillComponent] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [prepMsg, setPrepMsg] = useState("");
  const [prepared, setPrepared] = useState(null);
  const resetForNext = () => { setUploads([]); setSuggestions(null); setSel({}); setFields({ bezeichnung: "", manufacturer: "", modell: "", serial_no: "", auftragsnummer: "", baujahr: "" }); setSpecs({}); setStatus(""); setKind(""); setArtKey(""); setStep(1); };

  useEffect(() => {
    cachedGet("/assets").then((d) => setAssets(d || [])).catch(() => setAssets([]));
    cachedGet("/v1/catalogs/component-categories").then((d) => setCategories(d || [])).catch(() => setCategories([]));
    cachedGet("/v1/catalogs/component-types").then((d) => setTypes(d || [])).catch(() => setTypes([]));
    cachedGet("/v1/catalogs/component-option-lists").then((d) => setOptionLists(d || [])).catch(() => setOptionLists([]));
  }, []);

  useEffect(() => {
    if (!kind) { setChecklist(null); return; }
    cachedGet(`/v1/checkpoints?kind=${kind}`).then((d) => setChecklist(d || [])).catch(() => setChecklist(null));
  }, [kind]);

  const herstellerOpts = useMemo(() => optionLists.find((l) => l.key === "hersteller")?.items || [], [optionLists]);
  const statusOpts = useMemo(() => optionLists.find((l) => l.key === "komponenten_status")?.items || [], [optionLists]);

  const typesForArt = useMemo(() => types.filter((t) => t.art_key === artKey), [types, artKey]);
  const asset = assets.find((a) => a.id === assetId);
  useEffect(() => { loadCaptured(assetId); }, [assetId]);
  useEffect(() => { if (asset?.site_id) getPreparedStamp(asset.site_id).then(setPrepared); else setPrepared(null); }, [asset]); // eslint-disable-line react-hooks/exhaustive-deps

  const doPrepare = async () => {
    if (!navigator.onLine) { toast.info("Zum Vorbereiten wird einmalig Internet benötigt"); return; }
    setPreparing(true);
    try { const res = await prepareSite(asset, assets, setPrepMsg); setPrepared(res); toast.success(`Standort offline verfügbar: ${res.assets} Anlage(n), ${res.media} Foto(s)`); }
    catch (e) { toast.error("Vorbereitung fehlgeschlagen"); }
    setPreparing(false); setPrepMsg("");
  };

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
      setUploads((u) => [...u, { name: file.name, category, dataUrl }]);
    }
  };

  const uploadAll = async () => {
    const ids = [];
    const next = [...uploads];
    for (let i = 0; i < next.length; i++) {
      if (next[i].media_id) { ids.push(next[i].media_id); continue; }
      const fd = new FormData();
      fd.append("file", dataUrlToBlob(next[i].dataUrl), next[i].name);
      fd.append("category", next[i].category);
      fd.append("asset_id", assetId);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      next[i] = { ...next[i], media_id: data.id }; ids.push(data.id);
    }
    setUploads(next);
    return ids;
  };

  const extract = async () => {
    if (!uploads.length) return;
    if (!navigator.onLine) { toast.info("KI-Auslesung benötigt Internet — bitte Felder manuell ausfüllen"); return; }
    setBusy(true);
    try {
      const ids = await uploadAll();
      const { data } = await api.post("/v1/components/ocr-extract", { media_ids: ids, art_key: artKey, typ_key: kind });
      const sg = data.suggestions || [];
      setSuggestions(sg);
      const s = {}; sg.forEach((x) => { s[x.key] = { checked: true, value: x.value }; }); setSel(s);
      applyToFields(sg.map((x) => ({ ...x, value: x.value })));
      toast.success(`${sg.length} Feld(er) erkannt`);
      if (!sg.length) toast.info("Keine Werte erkannt — bitte manuell ausfüllen");
    } catch (e) { toast.error("KI-Auslesung fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) }); }
    setBusy(false);
  };

  const applyToFields = (chosen) => {
    const nf = { ...fields }; const ns = { ...specs };
    chosen.forEach((x) => { if (x.is_spec) ns[x.key] = x.value; else nf[x.key] = x.value; });
    if (!nf.bezeichnung) {
      const tn = types.find((t) => t.key === kind)?.name || "";
      nf.bezeichnung = [tn, nf.manufacturer].filter(Boolean).join(" ").trim();
    }
    setFields(nf); setSpecs(ns);
  };

  const save = async () => {
    if (!fields.bezeichnung.trim() || !assetId || !kind) return;
    setBusy(true);
    if (navigator.onLine) {
      try {
        const ids = await uploadAll();
        const { data: created } = await api.post(`/v1/assets/${assetId}/components`, {
          art_key: artKey, kind, bezeichnung: fields.bezeichnung.trim(),
          manufacturer: fields.manufacturer || null, modell: fields.modell || null,
          serial_no: fields.serial_no || null, auftragsnummer: fields.auftragsnummer || null, baujahr: fields.baujahr || null,
          betriebsstatus: status || null,
          specs, photos: ids, typenschild_media_id: ids[0] || null,
        });
        setSaved((n) => n + 1); toast.success("Komponente erfasst"); loadCaptured(assetId);
        const hasChecklist = checklist && checklist.length > 0;
        if (hasChecklist) { setFillComponent(created); setFillOpen(true); }
        resetForNext(); setBusy(false); return;
      } catch (e) {
        // Nur echte Netzwerkfehler in die Offline-Queue; echte API-Fehler (400/403/500) anzeigen
        if (e.response) {
          toast.error("Speichern fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) });
          setBusy(false); return;
        }
        // kein e.response → Netzwerkfehler → weiter in Offline-Queue
      }
    }
    try {
      await enqueue({ id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())),
        assetId, art_key: artKey, kind, fields: { ...fields, bezeichnung: fields.bezeichnung.trim() },
        specs, photos: uploads.map((u) => ({ name: u.name, category: u.category, dataUrl: u.dataUrl })), created_at: Date.now() });
      window.dispatchEvent(new Event("csp-queue-changed"));
      setSaved((n) => n + 1);
      toast.success("Offline gespeichert — wird bei Verbindung automatisch synchronisiert");
      resetForNext();
    } catch (e) { toast.error("Konnte nicht lokal gespeichert werden"); }
    setBusy(false);
  };

  useEffect(() => {
    const onDone = () => loadCaptured(assetId);
    window.addEventListener("csp-sync-done", onDone);
    return () => window.removeEventListener("csp-sync-done", onDone);
  }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const canNext = [!!assetId, !!(artKey && kind), true, !!fields.bezeichnung.trim()][step];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Bestandsaufnahme" subtitle="Komponente in unter einer Minute erfassen: Anlage → Art/Typ → Typenschild fotografieren → KI füllt aus → speichern" />

      <div className="mb-5 flex items-center gap-1" data-testid="erfassung-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-1">
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{i < step ? "✓" : i + 1}</div>
            <span className={`hidden text-xs sm:block ${i === step ? "font-semibold" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
          </div>
        ))}
      </div>

      {saved > 0 && <div className="mb-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700" data-testid="erfassung-saved-count">{saved} Komponente(n) in dieser Sitzung erfasst.</div>}

      {!online && <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700" data-testid="offline-banner"><span className="inline-flex items-center gap-1.5"><WifiSlash size={15} /> Offline-Modus — Erfassungen werden lokal gespeichert und später synchronisiert.</span></div>}
      {pending > 0 && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm" data-testid="pending-banner">
          <span>{pending} nicht synchronisierte Erfassung(en) lokal gespeichert.</span>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={!online || busy} data-testid="sync-now" onClick={sync}><CloudArrowUp size={14} /> Jetzt synchronisieren</Button>
        </div>
      )}

      <Card className="rounded-md border p-5">
        {step === 0 && (
          <div className="space-y-3" data-testid="step-asset">
            <Label2>Anlage wählen</Label2>
            <Select value={assetId} onValueChange={setAssetId}>
              <SelectTrigger data-testid="erfassung-asset"><SelectValue placeholder="Anlage auswählen …" /></SelectTrigger>
              <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.asset_number ? ` · ${a.asset_number}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Tipp: Über den QR-Scanner kannst du direkt die Anlage öffnen und hierher wechseln.</p>
            {assetId && (
              <div className="rounded-md border border-border bg-muted/40 p-3" data-testid="offline-prepare">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><span className="text-sm font-semibold">Offline-Einsatz</span>
                    <div className="text-xs text-muted-foreground">Standort dieser Anlage inkl. Komponenten &amp; Fotos für die Arbeit ohne Empfang laden.</div></div>
                  <Button size="sm" variant="outline" className="shrink-0 gap-1.5" disabled={preparing || !online} data-testid="offline-prepare-btn" onClick={doPrepare}><DownloadSimple size={14} /> {preparing ? (prepMsg || "…") : "Für Offline vorbereiten"}</Button>
                </div>
                {prepared && <div className="mt-1.5 text-xs text-emerald-700" data-testid="offline-prepared-info">✓ Offline vorbereitet: {prepared.assets} Anlage(n), {prepared.media} Foto(s){prepared.at ? ` · ${new Date(prepared.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}` : ""}</div>}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="step-arttyp">
            <div><Label2>Komponentenart</Label2>
              <Select value={artKey} onValueChange={(v) => { setArtKey(v); setKind(""); }}>
                <SelectTrigger data-testid="erfassung-art"><SelectValue placeholder="Art wählen …" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label2>Komponententyp</Label2>
              <Select value={kind} onValueChange={setKind} disabled={!artKey}>
                <SelectTrigger data-testid="erfassung-typ"><SelectValue placeholder="Typ wählen …" /></SelectTrigger>
                <SelectContent>{typesForArt.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {kind && checklist !== null && (
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="erfassung-checklist-preview">
                <div className="flex items-center gap-1.5 font-semibold">
                  <ListChecks size={15} weight="duotone" className="text-primary" />
                  Prüf-Checkliste
                  <Badge variant="outline" className="ml-1" data-testid="erfassung-checklist-count">{checklist.length} Prüfpunkt(e)</Badge>
                </div>
                {checklist.length > 0
                  ? <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                      {checklist.slice(0, 5).map((cp) => <li key={cp.id} className="truncate">· {cp.titel || cp.name || cp.beschreibung}</li>)}
                      {checklist.length > 5 && <li className="italic">… und {checklist.length - 5} weitere</li>}
                    </ul>
                  : <p className="mt-1 text-xs text-muted-foreground">Für diesen Komponententyp ist noch keine Prüf-Checkliste hinterlegt.</p>}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="step-photo">
            <Label2>Typenschild fotografieren</Label2>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 w-44" data-testid="erfassung-category"><SelectValue /></SelectTrigger>
                <SelectContent>{["Hauptschild", "Antriebsschild", "Steuerungsschild"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid="erfassung-upload-label">
                <Camera size={15} /> Foto aufnehmen
                <input type="file" accept="image/*" multiple capture="environment" className="hidden" data-testid="erfassung-file-input" onChange={(e) => handleFiles(e.target.files)} />
              </label>
              <Button size="sm" disabled={!uploads.length || busy} data-testid="erfassung-extract" onClick={extract}>{busy ? "…" : "KI auslesen"}</Button>
            </div>
            {uploads.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="erfassung-thumbs">
                {uploads.map((u, i) => <img key={i} src={u.dataUrl} alt={u.category} className="h-14 w-14 rounded-md border border-border object-cover" />)}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Mehrere Schilder (Haupt/Antrieb/Steuerung) können nacheinander aufgenommen und gemeinsam ausgewertet werden.</p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="step-save">
            <div><Label2>Bezeichnung *</Label2><Input data-testid="erfassung-bezeichnung" value={fields.bezeichnung} onChange={(e) => setFields({ ...fields, bezeichnung: e.target.value })} placeholder="z. B. Sektionaltor Halle A" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label2>Hersteller</Label2>
                <ManufacturerSelect testid="erfassung-hersteller" value={fields.manufacturer} onChange={(v) => setFields({ ...fields, manufacturer: v })} />
              </div>
              <div><Label2>Modell / Baureihe</Label2>
                <CatalogSelect listKey="modellreihe" filterGruppe={fields.manufacturer} value={fields.modell} onChange={(v) => setFields({ ...fields, modell: v })} testid="erfassung-modell" placeholder="Baureihe wählen …" />
              </div>
              <div><Label2>Seriennummer</Label2><Input value={fields.serial_no} onChange={(e) => setFields({ ...fields, serial_no: e.target.value })} /></div>
              <div><Label2>Auftragsnummer</Label2><Input data-testid="erfassung-auftragsnummer" value={fields.auftragsnummer} onChange={(e) => setFields({ ...fields, auftragsnummer: e.target.value })} /></div>
              <div><Label2>Baujahr</Label2><Input value={fields.baujahr} onChange={(e) => setFields({ ...fields, baujahr: e.target.value })} /></div>
              <div><Label2>Federtyp links</Label2>
                <CatalogSelect listKey="hoermann_federcode" filterGruppe="Links" value={specs.feder_links || ""} onChange={(v) => setSpecs({ ...specs, feder_links: v })} testid="erfassung-feder-links" placeholder="L…" />
              </div>
              <div><Label2>Federtyp rechts</Label2>
                <CatalogSelect listKey="hoermann_federcode" filterGruppe="Rechts" value={specs.feder_rechts || ""} onChange={(v) => setSpecs({ ...specs, feder_rechts: v })} testid="erfassung-feder-rechts" placeholder="R…" />
              </div>
            </div>
            {statusOpts.length > 0 && (
              <div><Label2>Status</Label2>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="erfassung-status"><SelectValue placeholder="Status wählen (optional) …" /></SelectTrigger>
                  <SelectContent>{statusOpts.map((o) => <SelectItem key={o.key} value={o.label}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {Object.keys(specs).length > 0 && (
              <div className="rounded-md border border-border p-2" data-testid="erfassung-specs-preview">
                <span className="text-xs text-muted-foreground">Technische Werte (KI):</span>
                <div className="mt-1 flex flex-wrap gap-1">{Object.entries(specs).map(([k, v]) => <Badge key={k} variant="outline" className="text-[10px]">{k}: {String(v)}</Badge>)}</div>
              </div>
            )}
            <div className="text-xs text-muted-foreground">Anlage: <b>{asset?.name}</b> · {uploads.length} Foto(s) werden verknüpft.</div>
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <Button variant="ghost" className="gap-2" disabled={step === 0} data-testid="erfassung-back" onClick={() => setStep((s) => Math.max(0, s - 1))}><ArrowLeft size={15} /> Zurück</Button>
          {step < 3
            ? <Button className="gap-2" disabled={!canNext} data-testid="erfassung-next" onClick={() => setStep((s) => s + 1)}>Weiter <ArrowRight size={15} /></Button>
            : <Button className="gap-2" disabled={!canNext || busy} data-testid="erfassung-save" onClick={save}><CheckCircle size={16} weight="bold" /> {busy ? "Speichern …" : "Komponente speichern"}</Button>}
        </div>
      </Card>

      {saved > 0 && step === 1 && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" className="gap-2" data-testid="erfassung-finish" onClick={() => navigate(`/anlage/${assetId}`)}><Stack size={15} /> Bestandsaufnahme beenden & Anlage öffnen</Button>
        </div>
      )}

      {assetId && captured.length > 0 && (
        <Card className="mt-4 rounded-md border p-4" data-testid="erfassung-captured">
          <Label2>Erfasste Komponenten dieser Anlage ({captured.length})</Label2>
          <div className="mt-2 space-y-1.5">
            {captured.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-md border border-border p-2" data-testid={`captured-${c.id}`}>
                {c.typenschild_media_id
                  ? <AuthImage mediaId={c.typenschild_media_id} alt={c.bezeichnung} className="h-10 w-10 rounded object-cover" />
                  : <div className="flex h-10 w-10 items-center justify-center rounded bg-muted"><PuzzlePiece size={16} className="text-muted-foreground" /></div>}
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{c.bezeichnung}</div><div className="text-xs text-muted-foreground">{c.manufacturer || ""}{c.baujahr ? ` · BJ ${c.baujahr}` : ""}{c.betriebsstatus ? ` · ${c.betriebsstatus}` : ""}</div></div>
                <Button size="sm" variant="outline" className="shrink-0 gap-1.5" data-testid={`captured-inspect-${c.id}`} onClick={() => { setFillComponent(c); setFillOpen(true); }}><ListChecks size={14} /> Prüfen</Button>
              </div>
            ))}
          </div>
        </Card>
      )}
      <ChecklistFillDialog open={fillOpen} onOpenChange={setFillOpen} component={fillComponent} onSaved={() => loadCaptured(assetId)} />
    </div>
  );
}
